import $ from "jquery";
import { showPageLoader, hidePageLoader } from "./page-loader";
import {
  updateCart,
  getSectionsToRender,
  checkCartCollectionsQtyLimits,
  handleLineErrors,
} from "./catifa-cart";
import {
  getWhiteGlovesShippingProductInCart,
  updateWhiteGlovesQty,
} from "./shipping";

const ON_CHANGE_DEBOUNCE_TIMER = 300; // Copied form src/base-theme/constants.js
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const chairPinSelectSelector = "select.chair-pin-select";
const qtyInputSelector = "catifa-quantity-input input.quantity__input";

export const manageChairPins = async () => {
  handleCartChairPinSelectChanged();
  handleCartUpdateItemQty();
  handleCartRemoveItem();
};

const handleCartChairPinSelectChanged = () => {
  document.addEventListener(
    "catifa-chair-pin-select-change",
    async function (event: Event) {
      if (event.target) {
        showPageLoader();
        try {
          const $chairPinSelect = $(event.target);
          const $quantityInput = $chairPinSelect
            .parents(".cart-item")
            .find(qtyInputSelector) as JQuery<HTMLInputElement>;
          const currentVal = $chairPinSelect.val();
          // remove all pins of current item form cart
          let updates: { [key: number]: number } = {};
          const $chairPinSelectOptions = $chairPinSelect.find("option");
          $chairPinSelectOptions.each(function () {
            const $option = $(this);
            if ($option.val() !== currentVal) {
              updates[Number($option.val())] = 0;
            }
          });
          const removeChairPinResponse = await fetch(
            window.Shopify.routes.root + "cart/update.js",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ updates }),
            },
          );
          if (removeChairPinResponse.ok) {
            // Emulate Debounce For Cart Updates, this is required to grant cart consistency
            await delay(ON_CHANGE_DEBOUNCE_TIMER);
            // add qty selected chair pins
            const response = await fetch(
              window.Shopify.routes.root + "cart/add.js",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  items: [
                    {
                      id: currentVal,
                      quantity: Number($quantityInput[0]?.value ?? 1),
                    },
                  ],
                  sections: getSectionsToRender().map(
                    (section) => section?.section,
                  ),
                  sections_url: window.location.pathname,
                }),
              },
            );
            await updateCart(
              response,
              $quantityInput.data("index"),
              $quantityInput[0]?.name,
            );
          } else {
            await updateCart(
              removeChairPinResponse,
              $quantityInput.data("index"),
              $quantityInput[0]?.name,
            );
          }
        } catch (e) {
          console.error(e);
        } finally {
          hidePageLoader();
        }
      }
    },
  );
};

const handleCartUpdateItemQty = () => {
  document.addEventListener(
    "catifa-cart-change",
    async function (event: Event) {
      if (event.target) {
        showPageLoader();
        try {
          const $quantityInput = $(event.target) as JQuery<HTMLInputElement>;
          const $cartItem = $quantityInput.closest(".cart-item");
          const $chairPinSelect = $cartItem
            .find(chairPinSelectSelector)
            .first() as JQuery<HTMLSelectElement>;
          const productVariantId = $quantityInput.data("quantityVariantId") as
            | string
            | undefined;
          if (productVariantId) {
            // check cart collections qty limits
            const collection_id_list = $quantityInput.data(
              "productCollectionList",
            );
            const quantity = Number($quantityInput.val());
            const previousQuantity = Number(
              $quantityInput.data("cart-quantity"),
            );
            const line = $quantityInput.data("index");
            // check collections limit only if quantity is not zero (removing items) or quantity > previous quantity (increasing)
            if (quantity > 0 && quantity > previousQuantity) {
              const checkCartCollectionsQtyResult =
                checkCartCollectionsQtyLimits(collection_id_list);
              if (checkCartCollectionsQtyResult.exceeded) {
                const response = await fetch(
                  window.Shopify.routes.root +
                    "cart?section_id=main-cart-items",
                  {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                  },
                );
                const parsedText = await response.text();
                const html = new DOMParser().parseFromString(
                  parsedText,
                  "text/html",
                );
                const source = html.querySelector("cart-items");
                if (source) {
                  $("cart-items").html(source.innerHTML);
                }
                const message = window.cartStrings.quantityLimitError.replace(
                  "[quantity]",
                  checkCartCollectionsQtyResult.qtyLimit?.toString() ?? "",
                );
                handleLineErrors(line, message);
                return;
              }
            }

            await updateProductAndChairPinQty(
              productVariantId.toString(), // id
              quantity, // qty
              line, // line
              $quantityInput[0]?.name, // name
              $chairPinSelect.val()?.toString(), // chairPinId
            );
          }
        } catch (e) {
          console.error(e);
        } finally {
          hidePageLoader();
        }
      }
    },
  );
};

const handleCartRemoveItem = () => {
  document.addEventListener(
    "catifa-cart-remove-button-clicked",
    async function (event: Event) {
      if (event.target) {
        showPageLoader();
        try {
          const $cartItem = $(event.target).parents(".cart-item");
          const $quantityInput = $cartItem.find(
            qtyInputSelector,
          ) as JQuery<HTMLInputElement>;
          const $chairPinSelect = $cartItem
            .find(chairPinSelectSelector)
            .first() as JQuery<HTMLSelectElement>;
          const productVariantId = $quantityInput.data("quantityVariantId") as
            | string
            | undefined;
          if (productVariantId) {
            await updateProductAndChairPinQty(
              productVariantId.toString(), // id
              0, // qty
              $quantityInput.data("index"), // line
              $quantityInput[0]?.name, // name
              $chairPinSelect.val()?.toString(), // chairPinId
            );
          }
        } catch (e) {
          console.error(e);
        } finally {
          hidePageLoader();
        }
      }
    },
  );
};

const updateProductAndChairPinQty = async (
  id: string,
  qty: number,
  line: string,
  name: string | undefined,
  chairPinId: string | undefined,
) => {
  let changes: {
    [key: string]:
      | string
      | number
      | [{ section: string | undefined; selector: string; id: string }]
      | undefined
      | (string | undefined)[];
  } = {};
  changes["id"] = id;
  changes["quantity"] = qty;
  changes["sections"] = getSectionsToRender().map(
    (section) => section?.section,
  );
  changes["sections_url"] = window.location.pathname;
  const responseProduct = await fetch(
    window.Shopify.routes.root + "cart/change.js",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(changes),
    },
  );
  // replicate logic from cart.js updateQuantity() function
  if (responseProduct) {
    // update chair pins in cart only if previous call was success
    if (responseProduct.ok) {
      // Emulate Debounce For Cart Updates, this is required to grant cart consistency
      await delay(ON_CHANGE_DEBOUNCE_TIMER);
      changes["id"] = chairPinId;
      const responseChairPin = await fetch(
        window.Shopify.routes.root + "cart/change.js",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(changes),
        },
      );
      const whiteGlovesProductId = getWhiteGlovesShippingProductInCart();
      if (responseChairPin.ok && whiteGlovesProductId) {
        // Emulate Debounce For Cart Updates, this is required to grant cart consistency
        await delay(ON_CHANGE_DEBOUNCE_TIMER);
        await updateWhiteGlovesQty(
          responseChairPin,
          whiteGlovesProductId,
          line,
          name,
        );
      } else {
        await updateCart(responseChairPin, line, name);
      }
    } else {
      await updateCart(responseProduct, line, name);
    }
  } else {
    const errors = document.getElementById("cart-errors");
    if (errors) {
      errors.textContent = window.cartStrings.error;
    }
    hidePageLoader();
  }
};
